import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {AccordionDetails, Link, MenuItem, Stack} from '@mui/material';
import {useEffect, useState} from 'react';
import {Control, Controller, FieldErrors, UseFormRegister, useFormState, useWatch} from 'react-hook-form';
import {PROFILE_FIELDS} from '../../constants/profile-fields.constants';
import {ProfileDirectorOption, ProfileFormValues, ProfilePayment} from '../../types/profile.types';
import {AccordionRoot, AccordionSummaryRoot, AccordionTitle, SectionTitle} from '../profile.styles';
import {SimpleAddressField} from '../simple-address-field';
import {SimpleField} from '../simple-field';
import {SimpleSelectField} from '../simple-select-field';
import {checkInn} from '../../../../shared/validation/innValidation';
import {validateOrgnOgnip} from '../../../../shared/validation/ogrnValidation';

const REQUIRED_FIELD_MESSAGE = 'Обязательное поле';
const BANK_DETAILS_MESSAGE = 'Заполните банковские реквизиты полностью';
const BIK_MESSAGE = 'БИК должен содержать 9 цифр';
const BANK_ACCOUNT_MESSAGE = 'Расчетный счет должен содержать 20 цифр';
const CORRESPONDENT_ACCOUNT_MESSAGE = 'Корреспондентский счет должен содержать 20 цифр';
const INN_MESSAGE = 'Некорректный ИНН';
const OGRN_MESSAGE = 'ОГРН должен содержать 13 или 15 цифр';
const KPP_MESSAGE = 'КПП должен содержать 9 цифр';
const KPP_REQUIRED_MESSAGE = 'КПП обязателен для ИНН из 10 цифр';
const ADD_USER_URL = 'https://demoservicedesk.konus.group/users';

type PaymentFieldName = 'rs' | 'bankName' | 'bik' | 'cor';

interface RequisitesSectionProps {
  control: Control<ProfileFormValues>;
  register: UseFormRegister<ProfileFormValues>;
  directors: ProfileDirectorOption[];
  directorsLoading: boolean;
}

const hasRequisitesErrors = (errors: FieldErrors<ProfileFormValues>) =>
  Boolean(
    errors.inn ||
      errors.ogrn ||
      errors.kpp ||
      errors.fullAddress ||
      errors.leaderUid ||
      errors.payment?.rs ||
      errors.payment?.bankName ||
      errors.payment?.bik ||
      errors.payment?.cor,
  );

const normalizePayment = (payment?: ProfilePayment) => ({
  rs: payment?.rs?.trim() || '',
  bankName: payment?.bankName?.trim() || '',
  bik: payment?.bik?.trim() || '',
  cor: payment?.cor?.trim() || '',
});

const validateBankField = (field: PaymentFieldName, value: string | undefined, payment?: ProfilePayment) => {
  const normalizedPayment = normalizePayment(payment);
  const hasAnyBankField = Object.values(normalizedPayment).some(Boolean);

  if (!hasAnyBankField) return true;
  if (!value?.trim()) return BANK_DETAILS_MESSAGE;

  const normalizedValue = value.trim();

  if (field === 'bik' && !/^\d{9}$/.test(normalizedValue)) return BIK_MESSAGE;
  if (field === 'rs' && !/^\d{20}$/.test(normalizedValue)) return BANK_ACCOUNT_MESSAGE;
  if (field === 'cor' && !/^\d{20}$/.test(normalizedValue)) return CORRESPONDENT_ACCOUNT_MESSAGE;

  return true;
};

export const RequisitesSection = ({control, register, directors, directorsLoading}: RequisitesSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const payment = useWatch({control, name: PROFILE_FIELDS.payment});
  const inn = useWatch({control, name: PROFILE_FIELDS.inn}) || '';
  const {errors} = useFormState({control});
  const shouldExpandByErrors = hasRequisitesErrors(errors);
  const showNoDirectorsMessage = !directorsLoading && directors.length === 0;

  useEffect(() => {
    if (shouldExpandByErrors) {
      setIsExpanded(true);
    }
  }, [shouldExpandByErrors]);

  return (
    <Stack gap="15px">
      <SectionTitle>Общая информация</SectionTitle>

      <SimpleField
        label="Наименование организации"
        {...register(PROFILE_FIELDS.fullCompanyName, {required: REQUIRED_FIELD_MESSAGE})}
        error={!!errors.fullCompanyName}
        helperText={errors.fullCompanyName?.message}
      />

      <Stack mt={1}>
        <AccordionRoot
          expanded={isExpanded}
          disableGutters
          elevation={0}
          onChange={(_, expanded) => setIsExpanded(expanded)}
        >
          <AccordionSummaryRoot expandIcon={<ExpandMoreIcon color="primary" />}>
            <AccordionTitle>Реквизиты организации</AccordionTitle>
          </AccordionSummaryRoot>

          <AccordionDetails sx={{p: 0, pt: '8px'}}>
            <Stack gap="15px">
              <Stack direction={{xs: 'column', md: 'row'}} gap="32px">
                <SimpleField
                  label="ИНН"
                  {...register(PROFILE_FIELDS.inn, {
                    required: REQUIRED_FIELD_MESSAGE,
                    validate: (value) => checkInn((value || '').trim()) || INN_MESSAGE,
                  })}
                  error={!!errors.inn}
                  helperText={errors.inn?.message}
                />
                <SimpleField
                  label="ОГРН"
                  {...register(PROFILE_FIELDS.ogrn, {
                    required: REQUIRED_FIELD_MESSAGE,
                    validate: (value) => {
                      const result = validateOrgnOgnip((value || '').trim(), inn.trim());
                      return result || OGRN_MESSAGE;
                    },
                  })}
                  error={!!errors.ogrn}
                  helperText={errors.ogrn?.message}
                />
              </Stack>

              <SimpleField
                label="КПП"
                {...register(PROFILE_FIELDS.kpp, {
                  validate: (value) => {
                    const normalizedValue = (value || '').trim();
                    const normalizedInn = inn.trim();

                    if (normalizedInn.length === 10 && !normalizedValue) {
                      return KPP_REQUIRED_MESSAGE;
                    }
                    if (!normalizedValue) return true;

                    return /^\d{9}$/.test(normalizedValue) || KPP_MESSAGE;
                  },
                })}
                error={!!errors.kpp}
                helperText={errors.kpp?.message}
              />

              <SimpleField
                label="Расчетный счет"
                {...register(`${PROFILE_FIELDS.payment}.${PROFILE_FIELDS.rs}`, {
                  validate: (value) => validateBankField('rs', value, payment),
                })}
                error={!!errors.payment?.rs}
                helperText={errors.payment?.rs?.message}
              />
              <SimpleField
                label="Наименование банка"
                {...register(`${PROFILE_FIELDS.payment}.${PROFILE_FIELDS.bankName}`, {
                  validate: (value) => validateBankField('bankName', value, payment),
                })}
                error={!!errors.payment?.bankName}
                helperText={errors.payment?.bankName?.message}
              />
              <SimpleField
                label="БИК"
                {...register(`${PROFILE_FIELDS.payment}.${PROFILE_FIELDS.bik}`, {
                  validate: (value) => validateBankField('bik', value, payment),
                })}
                error={!!errors.payment?.bik}
                helperText={errors.payment?.bik?.message}
              />
              <SimpleField
                label="Корреспондентский счет"
                {...register(`${PROFILE_FIELDS.payment}.${PROFILE_FIELDS.cor}`, {
                  validate: (value) => validateBankField('cor', value, payment),
                })}
                error={!!errors.payment?.cor}
                helperText={errors.payment?.cor?.message}
              />
              <SimpleAddressField label="Юридический адрес" control={control} name={PROFILE_FIELDS.fullAddress} />

              <Controller
                name={PROFILE_FIELDS.leaderUid}
                control={control}
                render={({field}) => (
                  <SimpleSelectField
                    label="ФИО руководителя"
                    {...field}
                    value={field.value || ''}
                    error={!!errors.leaderUid}
                    helperText={errors.leaderUid?.message}
                  >
                    {directors.map((director) => (
                      <MenuItem key={director.uid} value={director.uid}>
                        {director.fio}
                      </MenuItem>
                    ))}
                  </SimpleSelectField>
                )}
              />
              {showNoDirectorsMessage && (
                <Link href={ADD_USER_URL} target="_blank" rel="noopener noreferrer" underline="hover">
                  Добавить пользователя
                </Link>
              )}
            </Stack>
          </AccordionDetails>
        </AccordionRoot>
      </Stack>
    </Stack>
  );
};
